import * as React from 'react';
import {ReactNode, useContext, useState} from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import {Button} from '~/components/Button';
import {Intent} from '~/lib/intent';
import {useKeyPressCallback} from '~/lib/keyPress';
import {notify} from '~/lib/notify';
import style from './index.module.scss';

export type IConfirmOptions<T = any> = T & {
    intent?: Intent;
    icon?: ReactNode;
    onOk?: () => unknown;
    onNo?: () => void;
};

interface IConfirmContext {
    show: (message: ReactNode, options: IConfirmOptions) => void;
    close: () => void;
}

const ConfirmContext = React.createContext<IConfirmContext>({} as IConfirmContext);

type IConfirmState = {
    open: boolean;
    message: ReactNode | null;
    options: IConfirmOptions | null;
};

export const ConfirmProvider = (props: { children: ReactNode }) => {
    const [state, setState] = useState<IConfirmState>({open: false, message: null, options: null});
    const close = () => {
        setState({open: false, message: null, options: null});
    };
    useKeyPressCallback('Escape', () => close());
    return <ConfirmContext.Provider value={{
        show: (message, options) => {
            setState({open: true, message: message, options});
        },
        close: close
    }}>
        {props.children}
        <Dialog.Root open={state.open} onOpenChange={(open) => !open && close()}>
            <Dialog.Portal>
                <div className={style.dialogWrapper}>
                    <Dialog.Content className={style.dialog}>
                        <div className={style.content}>
                            <Dialog.Title className={style.message}>
                                {state.message}
                            </Dialog.Title>
                        </div>
                        <div className={style.controls}>
                            <Button
                                leftIcon={<i className='fa fa-times'/>}
                                onClick={() => {
                                    state.options?.onNo?.();
                                    close();
                                }}>Cancel</Button>
                            <Button
                                leftIcon={<i className='fa fa-check'/>}
                                intent={Intent.PRIMARY}
                                onClick={() => {
                                    const result = state.options?.onOk?.();
                                    if (result instanceof Promise) result.catch(error => notify.apiError(error));
                                    close();
                                }}>Ok</Button>
                        </div>
                    </Dialog.Content>
                </div>
            </Dialog.Portal>
        </Dialog.Root>
    </ConfirmContext.Provider>;
};


type IConfirmHook = <T>(message: ReactNode, options: IConfirmOptions) => void;

export function useConfirm(): IConfirmHook {
    const confirmContext = useContext(ConfirmContext);
    return (message, options) => confirmContext.show(message, options);
}


interface IMessageContext {
    show: (message: ReactNode, options?: IMessageOptions) => void;
    close: () => void;
}

const MessageContext = React.createContext<IMessageContext>({} as IMessageContext);


type IMessageOptions = {
    intent?: Intent;
    icon?: ReactNode;
    onOk?: () => void;
};

type IMessageHook = <T>(message: ReactNode, options?: IMessageOptions) => void;

export function useMessage(): IMessageHook {
    const messageContext = useContext(MessageContext);
    return (message, options) => messageContext.show(message, options);
}

type IMessageState = {
    open: boolean;
    message: ReactNode;
    options: IMessageOptions | null;
};


export const MessageProvider = (props: { children: ReactNode }) => {
    const [state, setState] = useState<IMessageState>({open: false, message: null, options: null});
    const close = () => {
        setState({open: false, message: null, options: null});
    };
    useKeyPressCallback('Escape', () => close());
    return <MessageContext.Provider value={{
        show: (message, options) => {
            setState({open: true, message: message, options: options ?? null});
        },
        close: close
    }}>
        {props.children}
        <Dialog.Root open={state.open} onOpenChange={(open) => !open && close()}>
            <Dialog.Portal>
                <div className={style.dialogWrapper}>
                    <Dialog.Content className={style.dialog}>
                        <div className={style.content}>
                            <Dialog.Title className={style.message}>
                                {state.message}
                            </Dialog.Title>
                        </div>
                        <div className={[style.controls, style.oneButton].join(' ')}>
                        <Button
                            leftIcon={<i className='fa fa-check'/>}
                            intent={Intent.PRIMARY}
                                onClick={() => {
                                    state.options?.onOk?.();
                                    close();
                                }}>Ok</Button>
                        </div>
                    </Dialog.Content>
                </div>
            </Dialog.Portal>
        </Dialog.Root>
    </MessageContext.Provider>;
};
